import {FC, useEffect, useMemo, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, PATCH, POST, PUT} from '../../../services/api'
import styles from './cashbackconfig.module.css'

type RuleKind = 'specific'
type RuleScope = 'variation' | 'product' | 'category' | 'catalog'
type BenefitKind = 'percent' | 'fixed'

// Novo tipo para incluir a aba de ranking sem quebrar o RuleScope
type TabKey = RuleScope | 'ranking'

type TargetItem = {
  id: number
  name: string
  label?: string
  image?: string
  code?: string
  status?: boolean
  categoryName?: string
  price?: number
  promotionalPrice?: number
  inventory?: number
  variationAttributes?: string[]
  productCount?: number
  dateStart?: string
  dateEnd?: string
}

type CashbackRuleRecord = {
  id: number
  kind: RuleKind
  scope: RuleScope
  targetId: number
  targetName: string
  targetData?: Record<string, any> | null
  benefitType?: BenefitKind
  benefitValue?: number
  validFrom: string | null
  validTo: string | null
  sku?: string
  status?: boolean
}

type CashbackRuleForm = {
  scope: RuleScope
  benefitType: BenefitKind
  benefitValue: number | string
  validFrom: string
  validTo: string
  status: boolean
}

type CashbackTableRow = CashbackRuleRecord & {
  scopeLabel: string
  benefitDisplay: string
}

const scopeLabels: Record<RuleScope, string> = {
  variation: 'Variação',
  product: 'Produto',
  category: 'Categoria',
  catalog: 'Catálogo',
}

const rankingLabels: Record<string, string> = {
  catalog: 'Catálogo',
  category: 'Categoria',
  product: 'Produto',
  variation: 'Variação',
}

const scopeIcons: Record<RuleScope, string> = {
  variation: 'bi-palette2',
  product: 'bi-box-seam',
  category: 'bi-tags',
  catalog: 'bi-journal-richtext',
}

const scopeToTypeCashback = (scope: RuleScope) => scope

const scopePageMeta: Record<RuleScope, {title: string; description: string}> = {
  variation: {
    title: 'Variação',
    description: 'Regras aplicadas diretamente em variações do produto.',
  },
  product: {
    title: 'Produto',
    description: 'Regras aplicadas diretamente em produtos.',
  },
  category: {
    title: 'Categoria',
    description: 'Regras aplicadas diretamente em categorias.',
  },
  catalog: {
    title: 'Catálogo',
    description: 'Regras aplicadas diretamente em catálogos.',
  },
}

const defaultForm = (scope: RuleScope = 'product'): CashbackRuleForm => ({
  scope,
  benefitType: 'percent',
  benefitValue: 0,
  validFrom: '',
  validTo: '',
  status: true,
})

const toDateOnly = (value: string | null) => {
  if (!value) return ''
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const formatMoney = (value?: number) => {
  if (value === undefined || value === null) return '-'
  return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(value)
}

const formatBenefit = (rule: CashbackRuleRecord) => {
  if (rule.benefitType === 'fixed') return formatMoney(rule.benefitValue)
  return `${rule.benefitValue ?? 0}%`
}

const isValidImageUrl = (value?: string) => !!value && /^https?:\/\//i.test(value)

const buildImageUrl = (path?: string, suffix?: string) => {
  if (!path) return ''
  if (!suffix) return path
  return `${path.replace(/\/+$/, '')}/${suffix}`
}

const getBestProductImage = (images: any[] = []) => {
  if (!Array.isArray(images) || images.length === 0) return ''
  const priorityOrder = ['mainpicture', 'imgsmall', 'thumbpicture', 'image']
  for (const type of priorityOrder) {
    const found = images.find((image) => image?.typeimg === type)
    if (found) return buildImageUrl(found.path, found.imgsuffix)
  }
  return buildImageUrl(images[0]?.path, images[0]?.imgsuffix)
}

const parseVariationAttributes = (context?: string): string[] => {
  if (!context) return []
  try {
    const parsed = JSON.parse(context)
    if (!parsed || typeof parsed !== 'object') return []
    return Object.entries(parsed).map(([key, value]) => `${key}: ${value}`)
  } catch {
    return []
  }
}

const getTargetDisplayName = (item: any): string => {
  const type = item?.typeCashback ?? item?.scope

  if (type === 'variation' && item?.targetData) {
    const productName = item.targetData.product?.name ?? '-'
    return productName
  }

  return (
    item?.targetData?.name ??
    item?.targetData?.catalog ??
    item?.targetName ??
    item?.name ??
    item?.product?.name ??
    item?.variation?.name ??
    item?.classification?.name ??
    item?.catalog?.name ??
    item?.catalog ??
    '-'
  )
}

const normalizeRule = (item: any, kind: RuleKind): CashbackRuleRecord => ({
  id: Number(item?.id ?? 0),
  kind,
  scope: (item?.typeCashback ?? item?.scope ?? 'product') as RuleScope,
  targetId: Number(item?.targetId ?? 0),
  targetName: getTargetDisplayName(item),
  targetData: item?.targetData ?? null,
  sku: item?.targetData?.sku ?? undefined,
  benefitValue: item?.cashbackRate ?? item?.benefitValue ?? 0,
  validFrom: item?.dateInit ?? null,
  validTo: item?.dateEnd ?? null,
  status: Boolean(item?.status),
})

const normalizeProductTarget = (item: any): TargetItem => ({
  id: Number(item?.id ?? 0),
  name: item?.name ?? '-',
  code: item?.code ?? '',
  status: Boolean(item?.status),
  categoryName: item?.classification?.name ?? '',
  image: getBestProductImage(item?.images),
})

const normalizeVariationTarget = (item: any): TargetItem => ({
  id: Number(item?.id ?? 0),
  name: item?.product?.name ?? '-',
  code: item?.sku ?? '',
  status: Boolean(item?.status),
  categoryName: item?.product?.classification?.name ?? '',
  price: item?.price,
  promotionalPrice: item?.promotionalPrice,
  inventory: item?.inventory,
  variationAttributes: parseVariationAttributes(item?.context),
  image: isValidImageUrl(item?.img) ? item.img : '',
})

const normalizeCategoryTarget = (item: any): TargetItem => ({
  id: Number(item?.id ?? 0),
  name: item?.name ?? '-',
  code: item?.code ?? '',
  status: Boolean(item?.status),
  image: isValidImageUrl(item?.img) ? item.img : '',
})

const normalizeCatalogTarget = (item: any): TargetItem => ({
  id: Number(item?.id ?? 0),
  name: item?.catalog ?? '-',
  productCount: Array.isArray(item?.products) ? item.products.length : undefined,
  dateStart: item?.dateStart ?? '',
  dateEnd: item?.dateEnd ?? '',
  image: isValidImageUrl(item?.imgCatalog) ? item.imgCatalog : '',
  label: item?.sellerCatalog ? 'Catálogo de vendedor' : '',
})

const ManagementContent: FC = () => {
  const {showWrapperModal, closeWrapperModal, wrapperModal} = useModals()
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [savingRule, setSavingRule] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('variation')
  const [specificRules, setSpecificRules] = useState<CashbackRuleRecord[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingKind, setEditingKind] = useState<RuleKind>('specific')
  const [form, setForm] = useState<CashbackRuleForm>(defaultForm())
  const [selectedTargets, setSelectedTargets] = useState<TargetItem[]>([])
  const [showSelected, setShowSelected] = useState(true)

  // Estados do Ranking
  const [rankingOrder, setRankingOrder] = useState<string[]>([])
  const [rankingLoading, setRankingLoading] = useState(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<TargetItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const currentRules = useMemo(
    () => (activeTab !== 'ranking' ? specificRules.filter((rule) => rule.scope === activeTab) : []),
    [specificRules, activeTab]
  )

  const currentTitle =
    activeTab !== 'ranking' ? scopePageMeta[activeTab].title : 'Ordem de Prioridade'
  const currentDescription =
    activeTab !== 'ranking'
      ? scopePageMeta[activeTab].description
      : 'Defina qual regra o sistema deve avaliar primeiro.'

  const tableRows = useMemo<CashbackTableRow[]>(
    () =>
      currentRules.map((rule) => ({
        ...rule,
        scopeLabel: scopeLabels[rule.scope],
        benefitDisplay: formatBenefit(rule),
      })),
    [currentRules]
  )

  const resetModalState = () => {
    setEditingId(null)
    setEditingKind('specific')
    if (activeTab !== 'ranking') {
      setForm(defaultForm(activeTab))
    }
    setSelectedTargets([])
    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    setShowSelected(true)
  }

  const loadRules = async () => {
    try {
      setTableLoading(true)
      const res = await GET(
        `/cashback-management?typeCashback=${scopeToTypeCashback(activeTab as RuleScope)}`
      )
      const dataArray = Array.isArray(res?.data) ? res.data : []

      setSpecificRules(dataArray.map((item: any) => normalizeRule(item, 'specific')))
    } catch (error) {
      console.error('Erro ao carregar regras de cashback:', error)
    } finally {
      setTableLoading(false)
    }
  }

  const loadRanking = async () => {
    try {
      setRankingLoading(true)
      const res = await GET('/cashback-priority-ranking')
      if (res?.data && res.data.length > 0) {
        setRankingOrder(res.data[0].rankingOrder)
      } else {
        setRankingOrder(['catalog', 'category', 'product', 'variation'])
      }
    } catch (error) {
      console.error('Erro ao carregar o ranking de prioridade:', error)
    } finally {
      setRankingLoading(false)
    }
  }

  const saveRanking = async () => {
    try {
      setRankingLoading(true)
      await POST('/cashback-priority-ranking', {
        rankingOrder: rankingOrder,
      })
      Swal.fire({
        icon: 'success',
        title: 'Ranking salvo',
        text: 'A ordem de prioridade foi atualizada com sucesso.',
      })
    } catch (error) {
      console.error('Erro ao salvar ranking:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível salvar o ranking. Tente novamente.',
      })
    } finally {
      setRankingLoading(false)
    }
  }

  const moveRankingItem = (index: number, direction: 'up' | 'down') => {
    const newRanking = [...rankingOrder]
    if (direction === 'up' && index > 0) {
      ;[newRanking[index - 1], newRanking[index]] = [newRanking[index], newRanking[index - 1]]
    } else if (direction === 'down' && index < newRanking.length - 1) {
      ;[newRanking[index + 1], newRanking[index]] = [newRanking[index], newRanking[index + 1]]
    }
    setRankingOrder(newRanking)
  }

  const init = async () => {
    try {
      if (activeTab === 'ranking') {
        await loadRanking()
      } else {
        await loadRules()
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading) {
      if (activeTab === 'ranking') {
        void loadRanking()
      } else {
        void loadRules()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useEffect(() => {
    const timer = setTimeout(() => {
      const query = searchQuery.trim()

      if (!wrapperModal.display || query.length < 2) {
        setSearchResults([])
        setSearchLoading(false)
        return
      }

      const runSearch = async () => {
        try {
          setSearchLoading(true)
          let results: TargetItem[] = []

          switch (form.scope) {
            case 'variation': {
              const response = await POST(
                `/variations/filter-admin?page=1&take=20&search=${encodeURIComponent(query)}`,
                {}
              )
              results = (response?.data ?? []).map(normalizeVariationTarget)
              break
            }
            case 'product': {
              const response = await POST(
                `/product/filter_admin?page=1&take=20&search=${encodeURIComponent(query)}`,
                {}
              )
              results = (response?.data ?? []).map(normalizeProductTarget)
              break
            }
            case 'category': {
              const response = await POST('/classification/filter', {status: true})
              results = (response?.data ?? [])
                .filter((item: any) =>
                  String(item?.name ?? '')
                    .toLowerCase()
                    .includes(query.toLowerCase())
                )
                .map(normalizeCategoryTarget)
              break
            }
            case 'catalog': {
              const response = await GET('/catalog')
              results = (response?.data ?? [])
                .filter((item: any) =>
                  String(item?.catalog ?? item?.name ?? '')
                    .toLowerCase()
                    .includes(query.toLowerCase())
                )
                .map(normalizeCatalogTarget)
              break
            }
            default:
              break
          }

          setSearchResults(
            results.filter((r) => !selectedTargets.some((t) => t.id === r.id)).slice(0, 20)
          )
        } catch (error) {
          console.error('Erro ao buscar itens do cashback:', error)
          setSearchResults([])
        } finally {
          setSearchLoading(false)
        }
      }

      void runSearch()
    }, 300)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scope, searchQuery, wrapperModal.display])

  const openCreateModal = (scope: RuleScope) => {
    setEditingKind('specific')
    setEditingId(null)
    setForm(defaultForm(scope))
    setSelectedTargets([])
    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    showWrapperModal(`Nova regra promocional - ${scopePageMeta[scope].title}`)
  }

  const openEditModal = (rule: CashbackRuleRecord) => {
    setEditingKind(rule.kind)
    setEditingId(rule.id)
    setForm({
      scope: rule.scope,
      benefitType: rule.benefitType ?? 'percent',
      benefitValue: rule.benefitValue !== undefined ? rule.benefitValue : 0,
      validFrom: toDateOnly(rule.validFrom),
      validTo: toDateOnly(rule.validTo),
      status: Boolean(rule.status),
    })

    setSelectedTargets([
      {
        id: rule.targetId,
        name: rule.targetName,
        code: rule.targetData?.sku ?? rule.targetData?.code,
      },
    ])

    setSearchQuery('')
    setSearchResults([])
    setErrors({})
    showWrapperModal(`Editar regra promocional - ${scopePageMeta[rule.scope].title}`)
  }

  const closeModal = () => {
    closeWrapperModal()
    resetModalState()
  }

  const setField = <K extends keyof CashbackRuleForm>(field: K, value: CashbackRuleForm[K]) => {
    setForm((prev) => ({...prev, [field]: value}))
    if (errors[field as string]) {
      const next = {...errors}
      delete next[field as string]
      setErrors(next)
    }
  }

  const validateForm = () => {
    const nextErrors: Record<string, string> = {}

    if (!form.scope) nextErrors.scope = 'Selecione o tipo do item.'
    if (selectedTargets.length === 0) nextErrors.target = 'Escolha ao menos um item alvo da regra.'

    const start = form.validFrom ? moment(form.validFrom) : null
    const end = form.validTo ? moment(form.validTo) : null

    if (start && !start.isValid()) nextErrors.validFrom = 'Data inicial inválida.'
    if (end && !end.isValid()) nextErrors.validTo = 'Data final inválida.'
    if (start && end && start.isValid() && end.isValid() && end.isBefore(start, 'day')) {
      nextErrors.validTo = 'A data final deve ser igual ou posterior à data inicial.'
    }

    const numericValue = Number(form.benefitValue)
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      nextErrors.benefitValue = 'Informe um valor válido maior que zero.'
    }
    if (form.benefitType === 'percent' && (numericValue <= 0 || numericValue > 99)) {
      nextErrors.benefitValue = 'O percentual precisa ser maior que 0 e menor ou igual a 99'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const saveRule = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Verifique os campos destacados antes de salvar.',
      })
      return
    }

    try {
      setSavingRule(true)

      // Monta os payloads baseados nos alvos selecionados
      const getPayloads = () =>
        selectedTargets.map((target) => {
          let finalValidFrom = form.validFrom
            ? moment.utc(form.validFrom).startOf('day').toISOString()
            : null

          let finalValidTo = form.validTo
            ? moment.utc(form.validTo).endOf('day').toISOString()
            : null

          if (form.validTo && !form.validFrom) {
            finalValidFrom = moment.utc().startOf('day').toISOString()
          }

          return {
            typeCashback: editingId ? form.scope : scopeToTypeCashback(activeTab as RuleScope),
            targetId: target.id,
            cashbackRate: Number(form.benefitValue),
            dateInit: finalValidFrom,
            dateEnd: finalValidTo,
            status: Boolean(form.status),
          }
        })

      if (editingId) {
        // Fluxo de edição mantido: usa PUT para a regra atual
        const payloads = getPayloads()
        const savePromises = payloads.map((payload) =>
          PUT(`/cashback-management/${editingId}`, payload)
        )
        await Promise.all(savePromises)
      } else {
        // Fluxo de criação: envia o array inteiro para a nova rota múltipla
        const payloads = getPayloads()
        await POST('/cashback-management/multiple', payloads)
      }

      closeModal()
      Swal.fire({
        icon: 'success',
        title: 'Registro salvo',
        text: 'A regra foi salva com sucesso.',
      })
    } catch (error) {
      console.error('Erro ao salvar regra de cashback:', error)

      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: String(error) || 'Não foi possível salvar a regra. Tente novamente.',
      })
    } finally {
      await loadRules()
      setSavingRule(false)
    }
  }

  const deleteRule = async (rule: CashbackRuleRecord) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Excluir registro?',
      text: 'Essa ação não pode ser desfeita.',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    })

    if (!confirmation.isConfirmed) return

    try {
      setLoading(true)
      await DELETE(`/cashback-management/${rule.id}`)
      await loadRules()
      Swal.fire({
        icon: 'success',
        title: 'Registro removido',
      })
    } catch (error) {
      console.error('Erro ao excluir regra de cashback:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível excluir o registro.',
      })
    } finally {
      setLoading(false)
    }
  }

  const switchStatus = async (Id: number, status: boolean) => {
    try {
      const managementBody = {
        id: Id,
        status: !status,
      }
      const switchCupom = await PATCH(`/cashback-management/management/${Id}`, managementBody)

      if (switchCupom.success) {
        const newManagement = specificRules.map((management: any) => {
          if (management.id === Id) {
            management.status = !status
          }
          return management
        })
        setSpecificRules(newManagement)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const renderSearchResultCard = (item: TargetItem, onClick: () => void) => {
    const hasImage = isValidImageUrl(item.image)
    const hasPriceInfo = item.price !== undefined || item.inventory !== undefined
    const hasPromo =
      item.promotionalPrice !== undefined &&
      item.price !== undefined &&
      item.promotionalPrice < item.price

    return (
      <div
        key={item.id}
        className={`d-flex align-items-center gap-3 p-3 rounded-2 border border-secondary border-opacity-25 bg-white ${styles.resultItem} cursor-pointer mb-2`}
        onClick={onClick}
      >
        <div className='symbol symbol-45px symbol-fixed flex-shrink-0'>
          {hasImage ? (
            <img src={item.image} alt={item.name} style={{objectFit: 'cover'}} />
          ) : (
            <div className='symbol-label bg-light-primary'>
              <i className={`bi ${scopeIcons[form.scope]} text-primary fs-4`}></i>
            </div>
          )}
        </div>

        <div className='flex-grow-1' style={{minWidth: 0}}>
          <div className='d-flex align-items-center gap-2'>
            <span className='fw-bold text-gray-800 text-truncate'>{item.name}</span>
            {item.status !== undefined && (
              <span
                className={`badge ${item.status ? 'badge-light-success' : 'badge-light-danger'}`}
              >
                {item.status ? 'Ativo' : 'Inativo'}
              </span>
            )}
          </div>

          <div className='d-flex flex-wrap align-items-center gap-2 mt-1'>
            {item.code && <span className='text-muted small'>Código: {item.code}</span>}
            {item.categoryName && (
              <span className='badge badge-light-secondary'>{item.categoryName}</span>
            )}
            {item.variationAttributes?.map((attribute) => (
              <span key={attribute} className='badge badge-light-info'>
                {attribute}
              </span>
            ))}
            {item.label && <span className='badge badge-light-warning'>{item.label}</span>}
          </div>

          {hasPriceInfo && (
            <div className='d-flex flex-wrap align-items-center gap-3 mt-1 fs-8 text-muted'>
              {item.price !== undefined && (
                <span>
                  Preço: <strong className='text-gray-700'>{formatMoney(item.price)}</strong>
                  {hasPromo && (
                    <span className='text-success ms-1'>
                      (Promo: {formatMoney(item.promotionalPrice)})
                    </span>
                  )}
                </span>
              )}
              {item.inventory !== undefined && <span>Estoque: {item.inventory}</span>}
            </div>
          )}
        </div>
        <span className='badge badge-light fw-normal flex-shrink-0'>ID {item.id}</span>
      </div>
    )
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Gestão e Regras</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Regras específicas e proteção de margem por item.
                </span>
              </h3>
              <div className='card-toolbar'>
                {activeTab !== 'ranking' && (
                  <Button
                    text='Nova Regra'
                    color='primary'
                    size='small'
                    icon='fas fa-plus'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={() => openCreateModal(activeTab as RuleScope)}
                    btnClass='fw-bolder'
                  />
                )}
              </div>
            </div>

            <div className='card-body'>
              <div className={`d-flex ${styles.tabMenu} flex-wrap`}>
                <div
                  className={`${styles.tabItem} ${
                    activeTab === 'variation' ? styles.tabActive : styles.tabInactive
                  }`}
                  onClick={() => setActiveTab('variation')}
                >
                  Variação
                </div>
                <div
                  className={`${styles.tabItem} ${
                    activeTab === 'product' ? styles.tabActive : styles.tabInactive
                  }`}
                  onClick={() => setActiveTab('product')}
                >
                  Produto
                </div>
                <div
                  className={`${styles.tabItem} ${
                    activeTab === 'category' ? styles.tabActive : styles.tabInactive
                  }`}
                  onClick={() => setActiveTab('category')}
                >
                  Categoria
                </div>
                <div
                  className={`${styles.tabItem} ${
                    activeTab === 'catalog' ? styles.tabActive : styles.tabInactive
                  }`}
                  onClick={() => setActiveTab('catalog')}
                >
                  Catálogo
                </div>
                <div
                  className={`${styles.tabItem} ${
                    activeTab === 'ranking' ? styles.tabActive : styles.tabInactivePriority
                  }`}
                  onClick={() => setActiveTab('ranking')}
                >
                  Prioridade
                </div>
              </div>

              {activeTab === 'ranking' ? (
                <div className='d-flex flex-column gap-4'>
                  <div>
                    <h4 className='fw-bolder mb-1'>{currentTitle}</h4>
                    <div className='text-muted'>
                      Defina qual regra o sistema deve avaliar primeiro. O item no topo tem
                      prioridade máxima. Se um produto não se encaixar na primeira regra, o sistema
                      tentará a segunda, e assim por diante.
                    </div>
                  </div>

                  {rankingLoading ? (
                    <Loading />
                  ) : (
                    <div className='row'>
                      <div className='col-md-12'>
                        <div className='d-flex flex-column gap-3'>
                          {rankingOrder.map((item, index) => (
                            <div
                              key={item}
                              className='d-flex align-items-center justify-content-between p-4 border rounded bg-white shadow-sm'
                            >
                              <div className='d-flex align-items-center gap-3'>
                                <div className='badge badge-light-primary fs-5'>{index + 1}º</div>
                                <span className='fw-bolder fs-5 text-gray-800'>
                                  {rankingLabels[item] || item}
                                </span>
                              </div>

                              <div className='d-flex gap-2'>
                                <button
                                  className='btn btn-icon btn-light btn-sm'
                                  onClick={() => moveRankingItem(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <i className='fas fa-chevron-up'></i>
                                </button>
                                <button
                                  className='btn btn-icon btn-light btn-sm'
                                  onClick={() => moveRankingItem(index, 'down')}
                                  disabled={index === rankingOrder.length - 1}
                                >
                                  <i className='fas fa-chevron-down'></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className='d-flex justify-content-end mt-10'>
                          <Button
                            text='Salvar'
                            color='primary'
                            size='small'
                            click={() => void saveRanking()}
                            disabled={rankingLoading}
                            btnClass='fw-bolder'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className='d-flex flex-column gap-4'>
                  <div>
                    <h4 className='fw-bolder mb-1'>{currentTitle}</h4>
                    <div className='text-muted'>{currentDescription}</div>
                  </div>

                  {tableLoading ? (
                    <Loading />
                  ) : (
                    <Datatable
                      data={tableRows}
                      headers={[
                        ...(activeTab === 'variation'
                          ? [{key: 'SKU', value: 'sku', type: 'default' as const}]
                          : []),
                        {key: 'Item', value: 'targetName', type: 'default'},
                        {
                          key: 'Status',
                          value: 'status',
                          type: 'check',
                          action: (item: any) => switchStatus(item.id, item.status),
                        },
                        {
                          key: 'Cashback',
                          value: 'benefitDisplay',
                          type: 'default',
                        },
                        {key: 'Data inicial', value: 'validFrom', type: 'date'},
                        {key: 'Data final', value: 'validTo', type: 'date'},
                        {key: 'Ações', value: 'acoes', type: '', noOrder: true},
                      ]}
                      options={[
                        {
                          icon: 'fas fa-pen',
                          action: (item) => openEditModal(item),
                          title: 'Editar regra promocional',
                        },
                        {
                          icon: 'fas fa-trash',
                          action: (item) => void deleteRule(item),
                          title: 'Excluir regra promocional',
                        },
                      ]}
                      color='primary'
                      showControls={true}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ModalWrapper
        confirmAction={() => void saveRule()}
        shouldCloseModal={false}
        disableMainButton={savingRule}
        onCloseAction={resetModalState}
        textAction={savingRule ? 'Salvando...' : editingId ? 'Atualizar' : 'Salvar'}
        zIndex={1050}
        width={120}
      >
        <div className='row g-4 mt-3'>
          {!editingId && (
            <div>
              <div className='col-12'>
                <h6 className='fw-bold text-gray-800 mb-3'>
                  <i className={`bi ${scopeIcons[form.scope]} text-primary me-2`}></i>
                  Seleção de itens para a regra
                </h6>
              </div>

              <div className='col-md-12'>
                <Input
                  inputAttr={{type: 'text'}}
                  label='Buscar item'
                  placeholder='Digite ao menos 2 caracteres para buscar...'
                  value={searchQuery}
                  change={(value: string) => setSearchQuery(value)}
                  disabled={!!editingId}
                  marginTop='0'
                />
                <div className='text-muted small mt-1'>
                  A busca é carregada de forma assíncrona conforme a aba selecionada.
                </div>
              </div>
              {selectedTargets.length > 0 && (
                <div className='col-12'>
                  <div
                    className='d-flex justify-content-between align-items-center mb-3 cursor-pointer'
                    onClick={() => setShowSelected(!showSelected)}
                  >
                    <h6 className='fw-bold text-gray-800 mb-0'>
                      Itens Selecionados ({selectedTargets.length})
                    </h6>
                    <button type='button' className='btn btn-sm btn-icon btn-light'>
                      <i className={`bi bi-chevron-${showSelected ? 'up' : 'down'}`}></i>
                    </button>
                  </div>

                  <div
                    style={{
                      maxHeight: showSelected ? '300px' : '0px',
                      opacity: showSelected ? 1 : 0,
                      overflowY: showSelected ? 'auto' : 'hidden',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <div className='d-flex flex-wrap gap-2 pb-2'>
                      {selectedTargets.map((target) => (
                        <span
                          key={target.id}
                          className='badge badge-light-primary d-flex align-items-center py-2 px-3 fs-6'
                        >
                          <span className='me-2 text-truncate' style={{maxWidth: '250px'}}>
                            {target.name}
                          </span>
                          <span className='text-muted fw-normal fs-8 me-3'>
                            Cód: {target.code || target.id}
                          </span>
                          {!editingId && (
                            <i
                              className='bi bi-x fs-3 cursor-pointer text-hover-danger ms-1'
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedTargets((prev) => prev.filter((t) => t.id !== target.id))
                              }}
                            ></i>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className='col-12'>
                <div className='d-flex justify-content-between align-items-center mb-3 mt-2'>
                  <h6 className='fw-bold text-gray-800 mb-0'>Resultados da pesquisa</h6>
                  <div className='d-flex align-items-center gap-3'>
                    {searchLoading && (
                      <span className='text-muted small d-flex align-items-center gap-2'>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        Pesquisando...
                      </span>
                    )}
                    {!searchLoading && searchResults.length > 1 && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary py-1 px-3'
                        onClick={() => {
                          setSelectedTargets((prev) => [...prev, ...searchResults])
                          setSearchResults([])
                        }}
                      >
                        Selecionar todos
                      </button>
                    )}
                  </div>
                </div>

                <div className={`border rounded-3 p-2 bg-light ${styles.resultsScroll}`}>
                  {searchQuery.trim().length >= 2 ? (
                    searchResults.length > 0 ? (
                      <div className='d-flex flex-column gap-2'>
                        {searchResults.map((item) =>
                          renderSearchResultCard(item, () => {
                            setSelectedTargets((prev) => [...prev, item])
                            setSearchResults((prev) => prev.filter((r) => r.id !== item.id))
                          })
                        )}
                      </div>
                    ) : (
                      <div className='text-center text-muted p-4'>
                        <i className='bi bi-search fs-2 d-block mb-2'></i>
                        Nenhum item encontrado para essa pesquisa.
                      </div>
                    )
                  ) : (
                    <div className='text-center text-muted p-4'>
                      <i className='bi bi-search fs-2 d-block mb-2'></i>
                      Digite ao menos dois caracteres para localizar o item.
                    </div>
                  )}
                </div>
                {errors.target && <div className='text-danger small mt-1'>{errors.target}</div>}
              </div>
              <div className='col-12'>
                <div className='separator separator-dashed'></div>
              </div>
            </div>
          )}

          <div className='col-12 mt-5'>
            <h6 className='fw-bold text-gray-800 mb-3'>
              <i className='bi bi-percent text-primary me-2'></i>
              Benefício
            </h6>
          </div>
          <div className='col-md-12'>
            <Input
              inputAttr={{type: 'number'}}
              label='Cashback (%)'
              value={String(form.benefitValue)}
              change={(value: string) => setField('benefitValue', parseFloat(value) || 0)}
              minNumber={0}
              maxNumber={99}
              allowFloat={true}
              marginTop='0'
            />
            {errors.benefitValue && (
              <div className='text-danger small mt-1'>{errors.benefitValue}</div>
            )}
          </div>
          <div className='col-md-12'>
            <Input
              inputAttr={{type: 'select'}}
              label='Status'
              value={String(form.status)}
              change={(value: string) => setField('status', value === 'true')}
              options={[
                {select: 'Ativo', value: 'true'},
                {select: 'Inativo', value: 'false'},
              ]}
              marginTop='0'
            />
          </div>
          <div className='col-12'>
            <div className='separator separator-dashed'></div>
          </div>

          <div className='col-12 mt-5 '>
            <h6 className='fw-bold text-gray-800 mb-3'>
              <i className='bi bi-calendar-range text-primary me-2'></i>
              Vigência (Opcional)
            </h6>
          </div>

          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'date'}}
              label='Data inicial'
              value={form.validFrom}
              change={(value: string) => setField('validFrom', value)}
              marginTop='0'
            />
            {errors.validFrom && <div className='text-danger small mt-1'>{errors.validFrom}</div>}
          </div>

          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'date'}}
              label='Data final'
              value={form.validTo}
              change={(value: string) => setField('validTo', value)}
              marginTop='0'
            />
            {errors.validTo && <div className='text-danger small mt-1'>{errors.validTo}</div>}
          </div>

          <div className='col-12'>
            <div className='alert alert-info d-flex align-items-start mb-0 mt-2'>
              <i className='bi bi-info-circle-fill fs-4 me-3 mt-1'></i>
              <div>
                Deixe a vigência em branco para a regra valer por tempo indeterminado. A regra
                promocional respeita prioridade por especificidade.
              </div>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  )
}

const CashbackManagement: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Cashback', path: 'cashback', isActive: false},
          {title: 'Gestão e Regras', path: 'cashback/management', isActive: true},
        ]}
      >
        Gestão e Regras
      </PageTitle>
      <ManagementContent />
    </>
  )
}

export {CashbackManagement}
